import { useState, useCallback } from 'react';
import type { CardData, MonsterInstance, HandCard, CardWithId } from '../../types/card';
import type { PlayerNumber } from '../../types/player';
import type {
  PendingHandSelection,
  PendingMonsterTarget,
  PendingGraveyardSelection,
  PendingDeckReview,
  PendingCageRelease,
  SelectedFieldCardInfo,
  MobileCardDetail,
} from '../../types/ui';
import type { BattleSelectionReturn } from '../../types/battle';

/**
 * useBattleSelection - バトルUI選択状態を管理するHook
 * Phase 3: magic-spirit.tsx から15個のuseStateを集約
 *
 * カテゴリ:
 * 1. 基本選択 - 手札/フィールドの選択状態
 * 2. 情報表示 - ホバー/詳細表示
 * 3. Pending選択 - 効果による選択待ち状態
 */
export const useBattleSelection = (): BattleSelectionReturn => {
  // ========================================
  // 基本選択（手札・フィールド）
  // ========================================
  const [selectedHandCard, setSelectedHandCard] = useState<HandCard | null>(null);
  const [selectedFieldMonster, setSelectedFieldMonster] = useState<number | null>(null);
  const [attackingMonster, setAttackingMonster] = useState<number | null>(null);
  const [selectedFieldCardInfo, setSelectedFieldCardInfo] = useState<SelectedFieldCardInfo | null>(null);

  // ========================================
  // 情報表示
  // ========================================
  const [hoveredCard, setHoveredCard] = useState<CardData | MonsterInstance | null>(null);
  const [mobileCardDetail, setMobileCardDetail] = useState<MobileCardDetail | null>(null);
  const [showGraveyardViewer, setShowGraveyardViewer] = useState<PlayerNumber | null>(null);

  // ========================================
  // Pending選択（効果による選択待ち）
  // ========================================
  // 手札選択待ち { message, callback, filter? }
  const [pendingHandSelection, setPendingHandSelection] = useState<PendingHandSelection | null>(null);
  // 手札選択モード中の選択カード
  const [pendingSelectedCard, setPendingSelectedCard] = useState<HandCard | null>(null);
  // 墓地選択待ち { message, callback, filter? }
  const [pendingGraveyardSelection, setPendingGraveyardSelection] = useState<PendingGraveyardSelection | null>(null);
  // 墓地選択中のカード
  const [pendingGraveyardSelectedCard, setPendingGraveyardSelectedCard] = useState<CardWithId | null>(null);
  // デッキトップ確認モーダル { cards, title, message, allowReorder, onConfirm, onCancel, selectMode, onSelect }
  const [pendingDeckReview, setPendingDeckReview] = useState<PendingDeckReview | null>(null);
  // モンスターターゲット選択 { message, callback, targetPlayer: 'opponent'|'self'|'both' }
  const [pendingMonsterTarget, setPendingMonsterTarget] = useState<PendingMonsterTarget | null>(null);
  // 選択中のモンスタースロット
  const [pendingSelectedMonsterIndex, setPendingSelectedMonsterIndex] = useState<number | null>(null);
  // 檻解放選択
  const [pendingCageRelease, setPendingCageRelease] = useState<PendingCageRelease | null>(null);

  // ========================================
  // ユーティリティ関数
  // ========================================

  /**
   * 基本選択をリセット（手札/フィールド/攻撃）
   */
  const resetSelection = useCallback((): void => {
    setSelectedHandCard(null);
    setSelectedFieldMonster(null);
    setAttackingMonster(null);
    setSelectedFieldCardInfo(null);
  }, []);

  /**
   * 全Pending選択をリセット
   */
  const resetAllPending = useCallback((): void => {
    setPendingHandSelection(null);
    setPendingSelectedCard(null);
    setPendingGraveyardSelection(null);
    setPendingGraveyardSelectedCard(null);
    setPendingDeckReview(null);
    setPendingMonsterTarget(null);
    setPendingSelectedMonsterIndex(null);
    setPendingCageRelease(null);
  }, []);

  /**
   * 手札選択を確定
   */
  const confirmHandSelection = useCallback((): void => {
    if (pendingHandSelection && pendingSelectedCard) {
      pendingHandSelection.callback([pendingSelectedCard]);
      setPendingHandSelection(null);
      setPendingSelectedCard(null);
    }
  }, [pendingHandSelection, pendingSelectedCard]);

  /**
   * 全状態をリセット（ゲーム初期化時用）
   */
  const resetAll = useCallback((): void => {
    // 基本選択
    setSelectedHandCard(null);
    setSelectedFieldMonster(null);
    setAttackingMonster(null);
    setSelectedFieldCardInfo(null);
    // 情報表示（明示的にリセット）
    setHoveredCard(null);
    setMobileCardDetail(null);
    setShowGraveyardViewer(null);
    // Pending選択
    setPendingHandSelection(null);
    setPendingSelectedCard(null);
    setPendingGraveyardSelection(null);
    setPendingGraveyardSelectedCard(null);
    setPendingDeckReview(null);
    setPendingMonsterTarget(null);
    setPendingSelectedMonsterIndex(null);
    setPendingCageRelease(null);
  }, []);

  // ========================================
  // Return
  // ========================================
  return {
    // State（読み取り用）
    selectedHandCard,
    selectedFieldMonster,
    attackingMonster,
    selectedFieldCardInfo,
    hoveredCard,
    mobileCardDetail,
    showGraveyardViewer,
    pendingHandSelection,
    pendingSelectedCard,
    pendingGraveyardSelection,
    pendingGraveyardSelectedCard,
    pendingDeckReview,
    pendingMonsterTarget,
    pendingSelectedMonsterIndex,
    pendingCageRelease,

    // Setters（個別更新用）
    setSelectedHandCard,
    setSelectedFieldMonster,
    setAttackingMonster,
    setSelectedFieldCardInfo,
    setHoveredCard,
    setMobileCardDetail,
    setShowGraveyardViewer,
    setPendingHandSelection,
    setPendingSelectedCard,
    setPendingGraveyardSelection,
    setPendingGraveyardSelectedCard,
    setPendingDeckReview,
    setPendingMonsterTarget,
    setPendingSelectedMonsterIndex,
    setPendingCageRelease,

    // ユーティリティ
    resetSelection,
    resetAllPending,
    resetAll,
    confirmHandSelection,
  };
};

export default useBattleSelection;
